import { Dialog, DialogActions, DialogContent } from "@mui/material";
import React, { useEffect, useState } from "react";
import { addUserValidation } from "../../utils/validation";
import * as api from "../../redux/api";
import { toast } from "react-toastify";
import { isYupError, parseYupError } from "../../utils/Yup";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Loader from "../loaders/Loader";
import { Country } from "country-state-city";
import { Countriess } from "../constants/contries";
import CustomSelect from "../Common/CustomSelect";

const initialState = {
  firstname: "",
  lastname: "",
  userName: "",
  email: "",
  password: "",
  country: "",
  organizationId: "",
  organizationType: ""
};

const AddUser = ({ handleClose, open, user }) => {
  const [eye, setEye] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(initialState);
  const [formError, setFormError] = useState(initialState);
  const [countries, setCountries] = useState([]);
  const [entityType, setEntityType] = useState("");
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  const handleCloseModal = () => {
    handleClose();
    setForm(initialState);
    setFormError(initialState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setFormError({ ...formError, [name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    try {
      setIsLoading(true);
      await addUserValidation.validate(form, { abortEarly: false });

      const payload = {
        ...form
      };

      const res = await api.registerUsers(payload);
      if (res.status === 200 || res.status === 201) {
        toast.success("Created Successfully");
        handleCloseModal();
      } else {
        toast.error(res?.error?.message);
      }
    } catch (error) {
      if (isYupError(error)) {
        setFormError(parseYupError(error));
      } else {
        toast.error(error?.response?.data?.message || "Something went wrong");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!entityType) {
      setOrganizations([]);
      return;
    }

    const fetchOrganizations = async () => {
      try {
        const res = await api.getOrganizationsByType(entityType);
        if (res.status === 200 && Array.isArray(res.data.data)) {
          setOrganizations(res.data.data);
        } else {
          setOrganizations([]);
          toast.error("No organizations found");
        }
      } catch (err) {
        toast.error("Failed to load organizations");
        setOrganizations([]);
      }
    };

    fetchOrganizations();
  }, [entityType]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      scroll="paper"
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <h4 className="text-center">Add User</h4>
          <div className="grid gap-4 mt-4">
            {/* First Name */}
            <div>
              <label className="block mb-1">First Name</label>
              <input
                type="text"
                name="firstname"
                onChange={handleChange}
                value={form.firstname}
                onKeyDown={(e) => {
                  if (!/^[a-zA-Z\s]*$/.test(e.key) && e.key.length === 1) {
                    e.preventDefault();
                  }
                }}
                maxLength={50}
                className="text-input w-full"
                placeholder="Enter First Name"
              />
              {formError.firstname && (
                <div className="form-eror">{formError.firstname}</div>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block mb-1">Last Name</label>
              <input
                type="text"
                name="lastname"
                onChange={handleChange}
                value={form.lastname}
                onKeyDown={(e) => {
                  if (!/^[a-zA-Z\s]*$/.test(e.key) && e.key.length === 1) {
                    e.preventDefault();
                  }
                }}
                maxLength={50}
                className="text-input w-full"
                placeholder="Enter Last Name"
              />
              {formError.lastname && (
                <div className="form-eror">{formError.lastname}</div>
              )}
            </div>

            <div>
              <label className="block mb-1">Username</label>
              <input
                type="text"
                name="userName"
                onChange={handleChange}
                value={form.userName}
                className="text-input w-full"
                placeholder="Enter Username"
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                autoComplete="new-username"
                autoCorrect="off"
                spellCheck="false"
              />
              {formError.userName && (
                <div className="form-eror">{formError.userName}</div>
              )}
            </div>
            <div>
              <label className="block mb-1">Entity Type</label>
              <select
                value={entityType}
                onChange={(e) => {
                  const value = e.target.value;
                  setEntityType(value);
                  setForm({
                    ...form,
                    organizationType: value,
                    organizationId: ""
                  });
                  setFormError({
                    ...formError,
                    organizationType: "",
                    organizationId: ""
                  });
                }}
                className="text-input w-full bg-white"
              >
                <option value="">-- Select Entity Type --</option>
                <option value="Mine">Mine</option>
                <option value="Refinery">Refinery</option>
                <option value="LogisticsProvider">Logistic</option>
                <option value="Vault">Vault</option>
              </select>
              {formError.organizationType && (
                <div className="form-eror">{formError.organizationType}</div>
              )}
            </div>
            {entityType && (
              <div>
                <label className="block mb-1">Organization</label>
                <select
                  name="organizationId"
                  value={form.organizationId || ""}
                  onChange={handleChange}
                  className="text-input w-full bg-white"
                >
                  <option value="">-- Select Organization --</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                {formError.organizationId && (
                  <div className="form-eror">{formError.organizationId}</div>
                )}
              </div>
            )}
            <div>
              <label className="block mb-1">Email</label>
              <input
                type="email"
                name="email"
                onChange={handleChange}
                value={form.email}
                className="text-input w-full"
                placeholder="Enter Email"
              />
              {formError.email && (
                <div className="form-eror">{formError.email}</div>
              )}
            </div>

            <div className="relative">
              <label className="block mb-1">Password</label>
              <input
                type={eye ? "text" : "password"}
                name="password"
                onChange={handleChange}
                value={form.password}
                className="text-input w-full pr-10"
                placeholder="Enter Password"
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
              <button
                type="button"
                className="absolute top-10 right-3 cursor-pointer"
                onClick={() => setEye(!eye)}
              >
                {eye ? (
                  <AiFillEye className="text-xl" />
                ) : (
                  <AiFillEyeInvisible className="text-xl" />
                )}
              </button>
              {formError.password && (
                <div className="form-eror">{formError.password}</div>
              )}
            </div>

            {/* <div>
              <label className="block mb-1">Country</label>
              <select
                name="country"
                onChange={handleChange}
                value={form.country}
                className="text-input w-full bg-white"
              >
                <option value="">-- Select Country --</option>
                {countries.map((country) => (
                  <option
                    key={country.isoCode}
                    value={country.name}
                    className="hover:bg-primary-100"
                  >
                    {country.name}
                  </option>
                ))}
              </select>
              {formError.country && (
                <div className="form-eror">{formError.country}</div>
              )}
            </div> */}
            <div>
              <label className="input-label !text-gray656">
                Country
                {/* <span className="text-primary-900">*</span> */}
              </label>
              <CustomSelect
                name="country"
                options={Countriess.map((item) => ({
                  value: item.value,
                  label: item.title
                }))}
                placeholder="Select a country "
                onChange={handleChange}
                defaultValue={form.countryOfIncorporation}
                disabled={false}
                className="mb-3 mt-1"
                dropdownClassName="border-mustard"
                optionClassName="hover:!bg-mustard"
                selectedOptionClassName="!bg-mustard"
              />
              {formError.country && (
                <div className="form-eror">{formError.country}</div>
              )}
            </div>
          </div>
        </DialogContent>

        <DialogActions className="flex justify-between ">
          <button
            type="button"
            onClick={handleCloseModal}
            className="btn btn-danger"
          >
            Close
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary"
          >
            {isLoading ? <Loader /> : "Submit"}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddUser;
